import { Button } from "@/components/ui/button";
import { useId, useState } from "react";
import { FileSearch, Loader2 } from "lucide-react";

import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { SkeletonRows } from "@/components/ui/skeleton";
import { useExamAnchor, useExamsWithoutOwner } from "@/hooks/use-exams";
import { errorCode } from "@/lib/error-code";
import { ExamLabels } from "@/lib/labels/exams";
import { djs } from "@/lib/djs";

import { PersonSearch, patientIdOf, type ChosenPerson } from "./person-search";
import { MetaLine, QueueRow, QueueSection } from "./queue-section";

type WithoutOwner = ReturnType<typeof useExamsWithoutOwner>["data"];

function WithoutOwnerRow({ doc }: { doc: NonNullable<WithoutOwner>[number] }) {
	const anchor = useExamAnchor();
	const hintId = useId();

	const [chosen, setChosen] = useState<ChosenPerson | null>(null);
	const [needsConfirm, setNeedsConfirm] = useState(false);

	const patientId = patientIdOf(chosen);
	const isLead = chosen?.kind === "lead";

	const submit = (confirmIdentity: boolean) => {
		if (patientId === null) {
			return;
		}

		anchor.mutate(
			{
				document_id: doc.id,
				patient_id: patientId,
				confirm_identity: confirmIdentity,
			},
			{
				onSuccess: () => {
					setNeedsConfirm(false);
					setChosen(null);
				},
				onError: (error) => {
					setNeedsConfirm(errorCode(error) === "REPORT_IDENTITY_MISMATCH");
				},
			},
		);
	};

	return (
		<QueueRow>
			<div className="flex flex-col gap-2">
				<p className="min-w-0 truncate text-sm font-medium text-foreground">
					{doc.reportName ?? ExamLabels.withoutOwner.unknownReportName}
				</p>

				<MetaLine>
					{doc.reportBirth !== null && <span className="font-mono">nasc. {doc.reportBirth}</span>}

					<span>{ExamLabels.status(doc.status)}</span>

					<span className="font-mono">{djs(doc.createdAt).format("DD/MM HH:mm")}</span>
				</MetaLine>

				<div className="flex flex-wrap items-center gap-2">
					<div className="min-w-48 flex-1">
						<PersonSearch
							chosen={chosen}
							onChoose={(person) => {
								setChosen(person);
								setNeedsConfirm(false);
							}}
							initialSearch={doc.reportName ?? ""}
							disabled={anchor.isPending}
						/>
					</div>

					<Button
						type="button"
						variant="default"
						size="sm"
						className="px-4"
						disabled={anchor.isPending || patientId === null}
						aria-describedby={isLead ? hintId : undefined}
						onClick={() => submit(false)}
					>
						{anchor.isPending && <Loader2 className="animate-spin" />}
						{anchor.isPending && "vinculando exame..."}
						{!anchor.isPending && ExamLabels.withoutOwner.anchor}
					</Button>

					{needsConfirm && patientId !== null && (
						<ConfirmationDialog
							tone="neutral"
							confirmLabel={ExamLabels.withoutOwner.confirmSame}
							open
							onOpenChange={(value) => {
								if (!value) {
									setNeedsConfirm(false);
								}
							}}
							title={ExamLabels.withoutOwner.confirmSame}
							pendingLabel={ExamLabels.withoutOwner.confirmingSame}
							isPending={anchor.isPending}
							onConfirm={() => submit(true)}
						/>
					)}
				</div>

				{isLead && (
					<p id={hintId} className="text-xs text-warning">
						{ExamLabels.suggested.needsPatient}
					</p>
				)}
			</div>
		</QueueRow>
	);
}

export function ExamWithoutOwner() {
	const docs = useExamsWithoutOwner();

	if (docs.data === undefined) {
		return <SkeletonRows rows={2} />;
	}

	return (
		<QueueSection title={ExamLabels.withoutOwner.title} subtitle={ExamLabels.withoutOwner.subtitle}>
			{docs.data.length === 0 && (
				<EmptyState
					icon={FileSearch}
					title={ExamLabels.withoutOwner.emptyTitle}
					description={ExamLabels.withoutOwner.emptyDescription}
				/>
			)}

			{docs.data.map((doc) => (
				<WithoutOwnerRow key={doc.id} doc={doc} />
			))}
		</QueueSection>
	);
}
